import type { ReactElement } from 'react';

import { Notice } from '@/components/admin/Notice';
import type { NotificationTemplate, RuleDef, TriggerDef } from '@/features/notifications/api';
import {
  AUDIENCE_LABELS,
  buildOffset,
  CALENDAR_TRIGGER_BASES,
  CONDITION_LABELS,
  formatOffset,
  parseOffset,
  TRIGGER_BASE_LABELS,
  WEEKDAY_LABELS,
  WEEKDAY_OPTIONS,
} from '@/features/notifications/format';

export function newPresetRule(existingKeys: string[]): RuleDef {
  let n = existingKeys.length + 1;
  while (existingKeys.includes(`rule_${n}`)) n += 1;
  return {
    key: `rule_${n}`,
    audience: 'participants',
    template: '',
    trigger: {
      base: 'event_start',
      offset: 'PT0S',
      at: null,
      day: null,
      weekday: null,
      business_day_shift: 'none',
    },
    condition: null,
    channels_by_pref: true,
  };
}

/**
 * 通知プリセットのルール編集フォーム（一覧モーダルから独立ページへ移設 / D-292）。
 */
export function PresetRulesEditor({
  rules,
  onChange,
  templates,
  jsonMode,
  jsonText,
  onJsonTextChange,
}: {
  rules: RuleDef[];
  onChange: (rules: RuleDef[]) => void;
  templates: NotificationTemplate[];
  jsonMode: boolean;
  jsonText: string;
  onJsonTextChange: (text: string) => void;
}): ReactElement {
  function updateRule(index: number, patch: Partial<RuleDef>): void {
    onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function updateTrigger(index: number, patch: Partial<TriggerDef>): void {
    onChange(
      rules.map((r, i) => (i === index ? { ...r, trigger: { ...r.trigger, ...patch } } : r)),
    );
  }

  function addRule(): void {
    onChange([...rules, newPresetRule(rules.map((r) => r.key))]);
  }

  function removeRule(index: number): void {
    onChange(rules.filter((_, i) => i !== index));
  }

  if (jsonMode) {
    return (
      <textarea
        value={jsonText}
        onChange={(e) => onJsonTextChange(e.target.value)}
        rows={20}
        className="w-full rounded-sm border border-wpborder px-3 py-2 font-mono text-sm"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {templates.length === 0 && (
        <Notice kind="info" dismissible={false}>
          通知テンプレートが1件もありません。先に「業務マスター &gt; 通知テンプレート」で作成してください。
        </Notice>
      )}
      {rules.map((rule, index) => {
        const offsetParts = parseOffset(rule.trigger.offset);
        const isCalendarBase = CALENDAR_TRIGGER_BASES.has(rule.trigger.base);
        return (
          <div key={`${rule.key}-${index}`} className="rounded-sm border border-wpborder bg-white p-3">
            <div className="mb-2 flex items-center gap-2">
              <label className="flex flex-1 items-center gap-1">
                <span className="whitespace-nowrap text-wptext-muted">ルールID</span>
                <input
                  value={rule.key}
                  onChange={(e) => updateRule(index, { key: e.target.value })}
                  className="w-full rounded-sm border border-wpborder px-3 py-2 font-mono text-sm"
                />
              </label>
              <button
                type="button"
                onClick={() => removeRule(index)}
                className="btn-secondary px-2 py-1 text-wpnotice-error"
              >
                このルールを削除
              </button>
            </div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <label className="block">
                <span className="mb-1.5 block text-wptext-muted">宛先</span>
                <select
                  value={rule.audience}
                  onChange={(e) => updateRule(index, { audience: e.target.value })}
                  className="w-full rounded-sm border border-wpborder px-3 py-2"
                >
                  {Object.entries(AUDIENCE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-wptext-muted">テンプレート</span>
                <select
                  value={rule.template}
                  onChange={(e) => updateRule(index, { template: e.target.value })}
                  className="w-full rounded-sm border border-wpborder px-3 py-2"
                >
                  <option value="">選択してください</option>
                  {templates.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.name}
                      {t.is_active ? '' : '（無効化中）'}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-wptext-muted">起点</span>
                <select
                  value={rule.trigger.base}
                  onChange={(e) => updateTrigger(index, { base: e.target.value })}
                  className="w-full rounded-sm border border-wpborder px-3 py-2"
                >
                  {Object.entries(TRIGGER_BASE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              {isCalendarBase ? (
                <>
                  {rule.trigger.base === 'monthly_day' && (
                    <label className="block">
                      <span className="mb-1.5 block text-wptext-muted">配信日（毎月・1〜28）</span>
                      <input
                        type="number"
                        min={1}
                        max={28}
                        value={rule.trigger.day ?? ''}
                        onChange={(e) =>
                          updateTrigger(index, {
                            day: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                        className="w-full rounded-sm border border-wpborder px-3 py-2"
                      />
                    </label>
                  )}
                  {rule.trigger.base === 'monthly_day' && (
                    <label className="block">
                      <span className="mb-1.5 block text-wptext-muted">休日シフト</span>
                      <select
                        value={rule.trigger.business_day_shift ?? 'none'}
                        onChange={(e) =>
                          updateTrigger(index, {
                            business_day_shift: e.target.value as 'none' | 'before' | 'after',
                          })
                        }
                        className="w-full rounded-sm border border-wpborder px-3 py-2"
                      >
                        <option value="none">固定（シフトなし）</option>
                        <option value="before">土日は前倒し</option>
                        <option value="after">土日は後ろ倒し</option>
                      </select>
                    </label>
                  )}
                  {rule.trigger.base === 'weekly' && (
                    <label className="block">
                      <span className="mb-1.5 block text-wptext-muted">配信曜日</span>
                      <select
                        value={rule.trigger.weekday ?? ''}
                        onChange={(e) =>
                          updateTrigger(index, { weekday: e.target.value || null })
                        }
                        className="w-full rounded-sm border border-wpborder px-3 py-2"
                      >
                        <option value="">選択してください</option>
                        {WEEKDAY_OPTIONS.map((w) => (
                          <option key={w} value={w}>
                            {WEEKDAY_LABELS[w]}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </>
              ) : (
                <label className="block">
                  <span className="mb-1.5 block text-wptext-muted">タイミング（起点から）</span>
                  <div className="flex flex-wrap items-center gap-1">
                    <select
                      value={offsetParts.direction}
                      onChange={(e) =>
                        updateTrigger(index, {
                          offset: buildOffset({
                            ...offsetParts,
                            direction: e.target.value as 'before' | 'after',
                          }),
                        })
                      }
                      className="rounded-sm border border-wpborder px-1 py-1"
                    >
                      <option value="before">前</option>
                      <option value="after">後</option>
                    </select>
                    <input
                      type="number"
                      min={0}
                      value={offsetParts.days}
                      onChange={(e) =>
                        updateTrigger(index, {
                          offset: buildOffset({
                            ...offsetParts,
                            days: Number(e.target.value) || 0,
                          }),
                        })
                      }
                      className="w-14 rounded-sm border border-wpborder px-1 py-1"
                    />
                    日
                    <input
                      type="number"
                      min={0}
                      value={offsetParts.hours}
                      onChange={(e) =>
                        updateTrigger(index, {
                          offset: buildOffset({
                            ...offsetParts,
                            hours: Number(e.target.value) || 0,
                          }),
                        })
                      }
                      className="w-14 rounded-sm border border-wpborder px-1 py-1"
                    />
                    時間
                    <input
                      type="number"
                      min={0}
                      value={offsetParts.minutes}
                      onChange={(e) =>
                        updateTrigger(index, {
                          offset: buildOffset({
                            ...offsetParts,
                            minutes: Number(e.target.value) || 0,
                          }),
                        })
                      }
                      className="w-14 rounded-sm border border-wpborder px-1 py-1"
                    />
                    分
                  </div>
                </label>
              )}

              <label className="block">
                <span className="mb-1.5 block text-wptext-muted">
                  配信時刻（空欄で起点の時刻のまま）
                </span>
                <input
                  type="time"
                  value={rule.trigger.at ?? ''}
                  onChange={(e) => updateTrigger(index, { at: e.target.value || null })}
                  className="w-full rounded-sm border border-wpborder px-3 py-2"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-wptext-muted">配信条件</span>
                <select
                  value={rule.condition ?? ''}
                  onChange={(e) => updateRule(index, { condition: e.target.value || null })}
                  className="w-full rounded-sm border border-wpborder px-3 py-2"
                >
                  <option value="">条件なし（常に送る）</option>
                  {Object.entries(CONDITION_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="mt-2 flex items-center gap-1 text-wptext-muted">
              <input
                type="checkbox"
                checked={rule.channels_by_pref}
                onChange={(e) => updateRule(index, { channels_by_pref: e.target.checked })}
              />
              Slack・Chatworkの個人設定に従って追加送信する（オフでメールのみ）
            </label>

            <p className="mt-2 text-wptext-muted">
              現在の設定: 宛先 {AUDIENCE_LABELS[rule.audience] ?? rule.audience} ／ 起点{' '}
              {TRIGGER_BASE_LABELS[rule.trigger.base] ?? rule.trigger.base}
              {formatOffset(rule.trigger.offset) ? ` の ${formatOffset(rule.trigger.offset)}` : ''}
              {rule.trigger.at ? ` ${rule.trigger.at}` : ''}
            </p>
          </div>
        );
      })}
      {rules.length === 0 && <p className="text-wptext-muted">ルールがありません。</p>}
      <button type="button" onClick={addRule} className="btn-secondary self-start">
        ルールを追加する
      </button>
    </div>
  );
}
